/**
 * @fileoverview ParametrosTintaRepository - Repositorio especializado para la gestión de parámetros de tinta
 */

import { DataRepository } from './base/DataRepository';
import type { 
  ParametrosTinta, 
  ParametrosTintaExpanded,
  CreateParametrosTintaData, 
  UpdateParametrosTintaData 
} from '../types/collections/ParametrosTinta';
import type { ListResult, QueryOptions } from '../types/common/PocketBaseTypes';
import { RepositoryError } from '../types/common/PocketBaseTypes';
import { createModuleLogger } from '../utils/logger';

/**
 * Repositorio especializado para la gestión de parámetros de tinta
 * Extiende DataRepository con métodos específicos del dominio
 */
export class ParametrosTintaRepository extends DataRepository<ParametrosTinta> {
  private logger = createModuleLogger('ParametrosTintaRepository');
  
  constructor() {
    super('parametros_tinta');
  }

  /**
   * Obtiene todos los parámetros de tinta activos con relaciones expandidas
   * Filtra por estado=true y ordena por nombre
   */
  async getAllActive(): Promise<ParametrosTinta[]> {
    try {
      const result = await this.pb.collection(this.collectionName).getFullList<ParametrosTinta>({
        filter: 'estado=true',
        sort: 'nombre',
        expand: 'tinta',
      });
      
      this.logger.debug('Obtuvidos parámetros de tinta activos', { count: result.length });
      return result;
    } catch (error) {
      throw new RepositoryError(
        `Error al obtener parámetros de tinta activos: ${error instanceof Error ? error.message : String(error)}`,
        'GET_ALL_ACTIVE_ERROR',
        error
      );
    }
  }

  /**
   * Busca parámetros de tinta por término de búsqueda
   * Busca en nombre y pantone
   */
  async searchByTerm(searchTerm: string): Promise<ParametrosTintaExpanded[]> {
    try {
      const result = await this.pb.collection(this.collectionName).getFullList<ParametrosTintaExpanded>({
        filter: `nombre ~ "${searchTerm}" || pantone ~ "${searchTerm}"`,
        sort: 'nombre',
        expand: 'tinta',
      });
      
      this.logger.debug('Búsqueda de parámetros de tinta por término', { 
        searchTerm, 
        count: result.length 
      });
      return result;
    } catch (error) {
      throw new RepositoryError(
        `Error al buscar parámetros de tinta por término "${searchTerm}": ${error instanceof Error ? error.message : String(error)}`,
        'SEARCH_BY_TERM_ERROR',
        error
      );
    }
  }

  /**
   * Obtiene todos los parámetros de tinta con relaciones expandidas
   */
  async getAllWithExpand(): Promise<ParametrosTintaExpanded[]> {
    try {
      const result = await this.pb.collection(this.collectionName).getFullList<ParametrosTintaExpanded>({
        sort: 'nombre',
        expand: 'tinta',
      });
      
      this.logger.debug('Parámetros de tinta obtenidos con expand', { 
        count: result.length 
      });
      return result;
    } catch (error) {
      throw new RepositoryError(
        `Error al obtener parámetros de tinta: ${error instanceof Error ? error.message : String(error)}`,
        'GET_ALL_ERROR',
        error
      );
    }
  }

  /**
   * Obtiene parámetros de tinta por tipo de tinta
   * Filtra por tinta específica y estado activo
   */
  async getByInkType(inkTypeId: string): Promise<ParametrosTinta[]> {
    try {
      const result = await this.pb.collection(this.collectionName).getFullList<ParametrosTinta>({
        filter: `tinta = "${inkTypeId}" && estado = true`,
        sort: 'nombre',
        expand: 'tinta',
      });
      
      this.logger.debug('Parámetros de tinta obtenidos por tipo', { 
        inkTypeId, 
        count: result.length 
      });
      return result;
    } catch (error) {
      throw new RepositoryError(
        `Error al obtener parámetros de tinta por tipo "${inkTypeId}": ${error instanceof Error ? error.message : String(error)}`,
        'GET_BY_INK_TYPE_ERROR',
        error
      );
    }
  }

  /**
   * Obtiene parámetros de tinta con paginación y filtros opcionales
   */
  async getListWithFilters(
    page: number = 1,
    pageSize: number = 10,
    filters: {
      search?: string;
      estado?: boolean;
      tinta?: string;
    } = {}
  ): Promise<ListResult<ParametrosTinta>> {
    try {
      const filterParts: string[] = [];

      if (filters.search) {
        filterParts.push(`(nombre ~ "${filters.search}" || pantone ~ "${filters.search}")`);
      }

      if (filters.estado !== undefined) {
        filterParts.push(`estado=${filters.estado}`);
      }

      if (filters.tinta) {
        filterParts.push(`tinta="${filters.tinta}"`);
      }

      const filter = filterParts.length > 0 ? filterParts.join(' && ') : '';

      const result = await this.pb.collection(this.collectionName).getList<ParametrosTinta>(
        page,
        pageSize,
        {
          filter,
          sort: 'nombre',
          expand: 'tinta',
        }
      );

      this.logger.debug('Lista de parámetros de tinta con filtros', {
        page,
        pageSize,
        filters,
        totalItems: result.totalItems,
      });

      return result;
    } catch (error) {
      throw new RepositoryError(
        `Error al obtener lista de parámetros de tinta con filtros: ${error instanceof Error ? error.message : String(error)}`,
        'GET_LIST_WITH_FILTERS_ERROR',
        error
      );
    }
  }

  /**
   * Crea un nuevo parámetro de tinta con validaciones
   */
  async createWithValidation(data: CreateParametrosTintaData): Promise<ParametrosTinta> {
    try {
      // Validaciones de negocio
      if (!data.nombre || data.nombre.trim() === '') {
        throw new Error('El nombre del parámetro de tinta es requerido');
      }

      if (!data.tinta) {
        throw new Error('El tipo de tinta es requerido');
      }

      // Verificar que los tipos de tinta existen y están activos
      if (data.tinta && data.tinta.length > 0) {
        for (const tintaId of data.tinta) {
          const tintaExists = await this.pb.collection('tinta').getOne(tintaId);
          if (!tintaExists || !tintaExists.estado) {
            throw new Error(`El tipo de tinta con ID ${tintaId} no existe o no está activo`);
          }
        }
      }

      // Verificar que no exista un parámetro con el mismo nombre y tipos de tinta
      const tintaFilter = data.tinta && data.tinta.length > 0 
        ? data.tinta.map(id => `tinta~"${id}"`).join(' || ')
        : 'tinta=""';
      const existingParametro = await this.findFirst(
        `nombre="${data.nombre.trim()}" && (${tintaFilter})`
      );
      if (existingParametro) {
        throw new Error(`Ya existe un parámetro de tinta con el nombre "${data.nombre}" para este tipo de tinta`);
      }

      // Asegurar que se crea como activo por defecto
      const dataToCreate = {
        ...data,
        nombre: data.nombre.trim(),
        pantone: data.pantone?.trim() || '',
        estado: data.estado !== undefined ? data.estado : true,
      };

      const result = await this.pb.collection(this.collectionName).create<ParametrosTinta>(dataToCreate);
      
      this.logger.info('Parámetro de tinta creado exitosamente', { 
        id: result.id, 
        nombre: result.nombre,
        tinta: result.tinta
      });

      return result;
    } catch (error) {
      throw new RepositoryError(
        `Error al crear parámetro de tinta: ${error instanceof Error ? error.message : String(error)}`,
        'CREATE_WITH_VALIDATION_ERROR',
        error
      );
    }
  }

  /**
   * Actualiza un parámetro de tinta con validaciones
   */
  async updateWithValidation(id: string, data: UpdateParametrosTintaData): Promise<ParametrosTinta> {
    try {
      // Obtener parámetro actual para validaciones
      const currentParametro = await this.pb.collection(this.collectionName)
        .getOne<ParametrosTinta>(id);

      if (!currentParametro) {
        throw new Error(`Parámetro de tinta con ID ${id} no encontrado`);
      }

      // Validaciones de negocio
      if (data.nombre !== undefined) {
        if (!data.nombre || data.nombre.trim() === '') {
          throw new Error('El nombre del parámetro de tinta no puede estar vacío');
        }

        // Verificar que no exista otro parámetro con el mismo nombre y tipo de tinta
        const tintaId = data.tinta || currentParametro.tinta;
        const existingParametro = await this.findFirst(
          `nombre="${data.nombre.trim()}" && tinta="${tintaId}" && id!="${id}"`
        );
        if (existingParametro) {
          throw new Error(`Ya existe otro parámetro de tinta con el nombre "${data.nombre}" para este tipo de tinta`);
        }
      }

      if (data.tinta !== undefined) {
        // Verificar que los nuevos tipos de tinta existen y están activos
        if (data.tinta.length > 0) {
          for (const tintaId of data.tinta) {
            const tintaExists = await this.pb.collection('tinta').getOne(tintaId);
            if (!tintaExists || !tintaExists.estado) {
              throw new Error(`El tipo de tinta con ID ${tintaId} no existe o no está activo`);
            }
          }
        }
      }

      // Preparar datos para actualización
      const dataToUpdate: UpdateParametrosTintaData = { ...data };
      if (data.nombre) {
        dataToUpdate.nombre = data.nombre.trim();
      }
      if (data.pantone !== undefined) {
        dataToUpdate.pantone = data.pantone.trim();
      }

      const result = await this.pb.collection(this.collectionName).update<ParametrosTinta>(id, dataToUpdate);
      
      this.logger.info('Parámetro de tinta actualizado exitosamente', { 
        id, 
        nombre: result.nombre,
        tinta: result.tinta
      });

      return result;
    } catch (error) {
      throw new RepositoryError(
        `Error al actualizar parámetro de tinta ${id}: ${error instanceof Error ? error.message : String(error)}`,
        'UPDATE_WITH_VALIDATION_ERROR',
        error
      );
    }
  }

  /**
   * Desactiva un parámetro de tinta (soft delete)
   */
  async deactivate(id: string): Promise<boolean> {
    try {
      await this.pb.collection(this.collectionName).update<ParametrosTinta>(id, { 
        estado: false 
      });
      
      this.logger.info('Parámetro de tinta desactivado exitosamente', { id });
      return true;
    } catch (error) {
      throw new RepositoryError(
        `Error al desactivar parámetro de tinta ${id}: ${error instanceof Error ? error.message : String(error)}`,
        'DEACTIVATE_ERROR',
        error
      );
    }
  }

  /**
   * Activa un parámetro de tinta
   */
  async activate(id: string): Promise<boolean> {
    try {
      await this.pb.collection(this.collectionName).update<ParametrosTinta>(id, { 
        estado: true 
      });
      
      this.logger.info('Parámetro de tinta activado exitosamente', { id });
      return true;
    } catch (error) {
      throw new RepositoryError(
        `Error al activar parámetro de tinta ${id}: ${error instanceof Error ? error.message : String(error)}`,
        'ACTIVATE_ERROR',
        error
      );
    }
  }

  /**
   * Obtiene parámetros de tinta con todas las relaciones expandidas
   */
  async getWithAllRelations(id: string): Promise<ParametrosTintaExpanded> {
    try {
      const result = await this.pb.collection(this.collectionName)
        .getOne<ParametrosTintaExpanded>(id, {
          expand: 'tinta',
        });

      this.logger.debug('Parámetro de tinta obtenido con relaciones', { id });
      return result;
    } catch (error) {
      throw new RepositoryError(
        `Error al obtener parámetro de tinta ${id} con relaciones: ${error instanceof Error ? error.message : String(error)}`,
        'GET_WITH_RELATIONS_ERROR',
        error
      );
    }
  }

  /**
   * Obtiene estadísticas básicas de parámetros de tinta
   */
  async getStats(): Promise<{
    totalActivos: number;
    totalInactivos: number;
    total: number;
    porTipo: Record<string, number>;
  }> {
    try {
      const [activos, inactivos, total, allActive] = await Promise.all([
        this.pb.collection(this.collectionName).getList<ParametrosTinta>(1, 1, {
          filter: 'estado=true'
        }),
        this.pb.collection(this.collectionName).getList<ParametrosTinta>(1, 1, {
          filter: 'estado=false'
        }),
        this.pb.collection(this.collectionName).getList<ParametrosTinta>(1, 1, {}),
        this.getAllActive()
      ]);

      // Contar por tipo de tinta
      const porTipo: Record<string, number> = {};
      allActive.forEach(parametro => {
        if (parametro.tinta && parametro.tinta.length > 0) {
          parametro.tinta.forEach(tintaId => {
            porTipo[tintaId] = (porTipo[tintaId] || 0) + 1;
          });
        } else {
          // Contar parámetros sin tinta asignada
          porTipo['sin_tinta'] = (porTipo['sin_tinta'] || 0) + 1;
        }
      });

      const stats = {
        totalActivos: activos.totalItems,
        totalInactivos: inactivos.totalItems,
        total: total.totalItems,
        porTipo,
      };

      this.logger.debug('Estadísticas de parámetros de tinta obtenidas', stats);
      return stats;
    } catch (error) {
      throw new RepositoryError(
        `Error al obtener estadísticas de parámetros de tinta: ${error instanceof Error ? error.message : String(error)}`,
        'GET_STATS_ERROR',
        error
      );
    }
  }
}
